/* ────────────────────────────────────────────────────────────────
   RS Referral Prompt — site-wide "Give $5, get $5" dialog

   Opens #rs-referral-modal (rendered by sections/rs-referral-popup.liquid,
   the same section that emits this tag, from the section group
   sections/rs-overlay-group.json) after the visitor has had the page in
   view for DELAY seconds. The delay and on/off switch ride on the loader
   tag. Which pages get it is decided by that section's page guard (not
   the cart, share, rest-easy, account or password pages); this file
   makes no page check of its own.

   ── TRIGGER (Digital #106, Sam 2026-11-02) ──────────────────────
   History: 3 s timer on the homepage (2026-10-03); half-page scroll and
   an ad / campaign-link / in-app skip (Digital #68 option 2A,
   2026-10-25). Now: a timer, setting "Open after", default 5 s, on every
   guarded page, for every visitor (the ad / in-app skip and its
   sessionStorage key rs_referral_skip are gone).

   Hidden tab: the timer only counts while the tab is visible. If the tab
   is hidden when the timer would fire, or is hidden at arm time, the full
   delay restarts once the tab is visible again, so it never opens into a
   page nobody is looking at (the swatch popup's rule).

   Modelled on assets/rs-swatch-prompt.js. It does NOT share that file's
   state: the rs_swatch_prompt shape is a two-writer contract with
   assets/rs-swatch-return.js and must not grow a third field.

   ── FREQUENCY ───────────────────────────────────────────────────
   State lives in localStorage under rs_referral_prompt as
   { shownAt, clicked }. localStorage is per origin, so the state is
   shared by every page and tab of the site:

     shownAt   ms timestamp of the last time the dialog actually
               opened. Written only on a confirmed open, so a skipped
               or failed fire never puts a visitor into cooldown.
     clicked   permanent, never expires. Set when the visitor clicks
               the button to the share page. Never shown again after.

   At most one show per 12 hours (REPEAT_MS; 24 h until 2026-11-02).
   Closing (X, Esc, backdrop, No thanks) is not recorded: the 12-hour
   repeat covers it. This file is the ONLY writer of rs_referral_prompt.
   Registered in docs/cross-file-registry.md.

   ── SKIP LIST ───────────────────────────────────────────────────
   At fire time it returns without opening, without writing state and
   without retrying this page view, if any of these hold:

     · this dialog or the swatch modal already opened this page view,
       by any route (footer trigger, swatch=1 deep link, swatch
       success return) — MutationObservers on both [open] attributes
     · any native dialog is open
     · the cart / favourites drawer is open (#rs-drawer.rs-drawer--open,
       owned by assets/cart-popup.js)
     · the menu drawer is open (body.rs-menu-open, owned by
       assets/rs-menu.js)
     · a Dawn modal is open (body.overflow-hidden, set by global.js
       ModalDialog / details modals: PDP pop-ups, quick add, search)
     · focus is in a field the visitor is typing in

   The next page view tries again (state was not written). It also never
   arms on a swatch post-submit return load (the swatch modal renders its
   success element and rs-swatch-return.js reopens it).

   ── ORDERING DEPENDENCY ─────────────────────────────────────────
   Must run before assets/rs-swatch-deeplink.js (emitted from the
   footer) so the swatch-modal observer is attached before the deep
   link opens it on a macrotask. Both are deferred, so document order
   decides: layout/theme.liquid renders the overlay group before the
   footer group.
   ──────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  if (window.rsReferralPromptLoaded) return; // re-entrancy guard (Theme Editor reload safe)
  window.rsReferralPromptLoaded = true;

  var MODAL_ID = 'rs-referral-modal';
  var SWATCH_MODAL_ID = 'rs-swatch-modal'; // owned by snippets/rs-swatch-request.liquid
  var STORE_KEY = 'rs_referral_prompt';
  var REPEAT_MS = 12 * 60 * 60 * 1000; // 12 hours between shows (Digital #106)
  var DEFAULT_DELAY = 5;               // seconds, when the tag says nothing usable

  var TYPING_SELECTOR =
    'input:not([type=button]):not([type=submit]):not([type=checkbox]):not([type=radio]), ' +
    'textarea, select, [contenteditable="true"]';

  /* ── persistence: rs_referral_prompt = { shownAt, clicked } ── */
  function readState() {
    var s = {};
    try {
      var raw = localStorage.getItem(STORE_KEY);
      var parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object') s = parsed;
    } catch (e) { s = {}; } // private mode / malformed JSON — treat as fresh
    return {
      shownAt: typeof s.shownAt === 'number' ? s.shownAt : 0,
      clicked: !!s.clicked
    };
  }

  function writeState(patch) {
    try {
      var s = readState();
      if (Object.prototype.hasOwnProperty.call(patch, 'shownAt')) s.shownAt = patch.shownAt;
      if (Object.prototype.hasOwnProperty.call(patch, 'clicked')) s.clicked = !!patch.clicked;
      localStorage.setItem(STORE_KEY, JSON.stringify({ shownAt: s.shownAt, clicked: s.clicked }));
    } catch (e) { /* private mode / quota — non-fatal */ }
  }

  function config() {
    var el = document.querySelector('script[data-rs-referral-prompt]');
    var delay = el ? parseInt(el.dataset.delay, 10) : NaN;
    if (isNaN(delay) || delay < 1 || delay > 60) delay = DEFAULT_DELAY;
    return {
      enabled: !!(el && el.dataset.enabled === 'true'),
      delay: delay
    };
  }

  function watchOpen(dialog, onOpen) {
    if (!dialog || typeof MutationObserver !== 'function') return;
    new MutationObserver(function () {
      if (dialog.open) onOpen();
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  function init() {
    var modal = document.getElementById(MODAL_ID);
    if (!modal) return;

    /* Wiring that holds whichever way the dialog was opened. */
    modal.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('[data-rs-referral-cta]')) {
        writeState({ clicked: true }); // navigation continues normally
        return;
      }
      if (t.closest('[data-rs-referral-dismiss]')) {
        if (window.rsModal && typeof window.rsModal.close === 'function') {
          window.rsModal.close(MODAL_ID);
        } else if (typeof modal.close === 'function') {
          modal.close();
        }
      }
    });

    var cfg = config();
    if (!cfg.enabled) return;

    /* Swatch post-submit return: that load belongs to the swatch modal. */
    if (document.querySelector('#' + SWATCH_MODAL_ID + ' [data-rs-form-success]')) return;

    var state = readState();
    if (state.clicked) return;                           // already went to the share page
    if (Date.now() - state.shownAt < REPEAT_MS) return;  // shown within the last 12h

    var openedThisView = false;
    function markOpened() { openedThisView = true; }
    watchOpen(modal, markOpened);
    watchOpen(document.getElementById(SWATCH_MODAL_ID), markOpened);

    /* The show is armed, so fetch the photo now rather than on open. */
    var img = modal.querySelector('[data-rs-referral-img]');
    if (img) img.loading = 'eager';

    function busy() {
      if (openedThisView) return true;
      if (document.querySelector('dialog[open]')) return true;
      if (document.querySelector('#rs-drawer.rs-drawer--open')) return true;
      if (document.body && document.body.classList.contains('rs-menu-open')) return true;
      if (document.body && document.body.classList.contains('overflow-hidden')) return true;
      var active = document.activeElement;
      if (active && active.matches && active.matches(TYPING_SELECTOR)) return true;
      return false;
    }

    function openPrompt() {
      if (!window.rsModal || typeof window.rsModal.open !== 'function') return;
      window.rsModal.open(MODAL_ID);
      /* Record the show only once the dialog is genuinely open. */
      if (modal.open) writeState({ shownAt: Date.now() });
    }

    function fire() {
      /* Tab went away during the delay: restart the full delay rather
         than opening into a page nobody is looking at. */
      if (document.hidden) { arm(); return; }
      if (busy()) return;                     // one attempt per page view
      /* Another tab may have shown it, or the visitor clicked through,
         during the delay: the state is site-wide, so read it again. */
      var now = readState();
      if (now.clicked || Date.now() - now.shownAt < REPEAT_MS) return;
      openPrompt();
    }

    function arm() {
      if (document.hidden) {
        document.addEventListener('visibilitychange', function onVisible() {
          if (document.hidden) return;
          document.removeEventListener('visibilitychange', onVisible);
          arm();
        });
        return;
      }
      setTimeout(fire, cfg.delay * 1000);
    }

    arm();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
